import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Tooltip, Paper, TableSortLabel,
    TextField, InputAdornment, Box, Button, Typography, Checkbox,
} from "@mui/material";
import {
    ExpandMore, ExpandLess, CancelRounded, Search as SearchIcon, FilterAltOff,
} from "@mui/icons-material";
import ActionChip from "../../Widgets/ActionChip/ActionChip";
import '../../../pages/Document/Document.scss';

const headerStyle = {
    backgroundColor: 'var(--foundation-blue-light-active, #BFC8FF)',
    color: "var(--Foundation-Blue-Normal, #304FFF)",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontWeight: 'bold',
    lineHeight: "normal",
    height: '56px',
    minHeight: '50px',
    padding: "0 5px",
};

const filterCellStyle = {
    backgroundColor: '#F5F5F6',
    padding: '4px 6px',
    verticalAlign: 'top',
};

const EMPTY_FILTERS = {
    matchNumber: '',
    transactionDate: '',
    matchDate: '',
    description: '',
    reference: '',
    documentDate: '',
    amount: '',
    type: '',
    debitAccount: '',
    creditAccount: '',
    uploadNumber: '',
};

const getRowSx = (row) => {
    if (row.isHeader) {
        return { backgroundColor: "#EBF2FF", fontWeight: "bold" };
    }
    if (row.isSubRow) {
        return { backgroundColor: "#F7F9FC" };
    }
    return {};
};

function getParentTransactions(parent) {
    if (parent.externalTransactions?.length) return parent.externalTransactions;
    if (parent.internalTransactions?.length) return parent.internalTransactions;
    return [];
}

function getParentTotalAmount(parent) {
    return getParentTransactions(parent).reduce(
        (sum, tx) => sum + (Number(tx.amount) || 0),
        0
    );
}

/** The distinct values a match carries across its transactions, so a collapsed row still says something. */
function distinctOf(parent, pick) {
    const all = [
        ...(parent.externalTransactions || []),
        ...(parent.internalTransactions || []),
    ];
    return [...new Set(all.map(pick).filter((value) => value != null && value !== ''))];
}

/** Up to two values, with a count for the rest — a collapsed row has one line to say it in. */
function summarizeValues(values) {
    if (values.length === 0) return '';
    if (values.length <= 2) return values.join(', ');
    return `${values.slice(0, 2).join(', ')} +${values.length - 2}`;
}

/** How many entries each side of a match holds: the first thing to check before undoing one. */
function sideCountsLabel(parent, t) {
    const bank = parent.externalTransactions?.length || 0;
    const book = parent.internalTransactions?.length || 0;
    const parts = [];
    if (bank) parts.push(`${t('BankCommands')}: ${bank}`);
    if (book) parts.push(`${t('BookCommands')}: ${book}`);
    return parts.join('  •  ');
}

function formatTypeLabel(type, t) {
    if (!type) return '';
    const parts = type.split('-');
    return parts.length > 1 ? t(parts[1]) : t(type);
}

function collectSearchableTexts(parent, formatDate, formatAccountLabel, t) {
    const texts = [];
    const push = (v) => {
        if (v != null && v !== '') texts.push(String(v).toLowerCase());
    };

    push(parent.matchNumber);
    push(formatDate(parent.transactionDate));
    push(formatDate(parent.matchDate));
    push(parent.description);
    push(parent.reference);

    const allTx = [
        ...(parent.externalTransactions || []),
        ...(parent.internalTransactions || []),
    ];
    allTx.forEach((tx) => {
        push(tx.description);
        push(tx.reference);
        push(tx.reference2);
        push(tx.amount);
        push(formatDate(tx.documentDate));
        push(tx.uploadNumber);
        push(formatTypeLabel(tx.type, t));
        push(tx.debitAccount);
        push(tx.creditAccount);
        if (formatAccountLabel) {
            push(formatAccountLabel(tx.debitAccount));
            push(formatAccountLabel(tx.creditAccount));
        }
    });
    return texts;
}

function parentMatchesGlobalSearch(parent, lowerSearch, formatDate, formatAccountLabel, t) {
    if (!lowerSearch) return true;
    return collectSearchableTexts(parent, formatDate, formatAccountLabel, t)
        .some((text) => text.includes(lowerSearch));
}

function parentMatchesColumnFilters(parent, filters, formatDate, formatAccountLabel, t) {
    const active = Object.entries(filters).filter(([, v]) => v.trim());
    if (active.length === 0) return true;

    const textsByField = {
        matchNumber: [String(parent.matchNumber ?? '')],
        transactionDate: [formatDate(parent.transactionDate)],
        matchDate: [formatDate(parent.matchDate)],
        description: [],
        reference: [],
        documentDate: [],
        amount: [String(getParentTotalAmount(parent))],
        type: [],
        debitAccount: [],
        creditAccount: [],
        uploadNumber: [],
    };

    const allTx = [
        ...(parent.externalTransactions || []),
        ...(parent.internalTransactions || []),
    ];
    allTx.forEach((tx) => {
        textsByField.description.push(tx.description ?? '');
        textsByField.reference.push(tx.reference ?? '');
        textsByField.reference.push(tx.reference2 ?? '');
        textsByField.documentDate.push(formatDate(tx.documentDate));
        textsByField.amount.push(String(tx.amount ?? ''));
        textsByField.type.push(formatTypeLabel(tx.type, t));
        textsByField.uploadNumber.push(String(tx.uploadNumber ?? ''));
        textsByField.debitAccount.push(tx.debitAccount ?? '');
        textsByField.creditAccount.push(tx.creditAccount ?? '');
        if (formatAccountLabel) {
            textsByField.debitAccount.push(formatAccountLabel(tx.debitAccount));
            textsByField.creditAccount.push(formatAccountLabel(tx.creditAccount));
        }
    });

    return active.every(([field, filterVal]) => {
        const q = filterVal.trim().toLowerCase();
        const values = (textsByField[field] || []).map((v) => String(v).toLowerCase());
        return values.some((v) => v.includes(q));
    });
}

function getSortValue(parent, key, formatDate, formatAccountLabel, t) {
    const txs = getParentTransactions(parent);
    const firstTx = txs[0];

    switch (key) {
        case 'matchNumber':
            return parent.matchNumber ?? 0;
        case 'transactionDate':
            return new Date(parent.transactionDate || 0).getTime();
        case 'matchDate':
            return new Date(parent.matchDate || 0).getTime();
        case 'description':
            return (firstTx?.description ?? parent.description ?? '').toLowerCase();
        case 'reference':
            return (firstTx?.reference ?? parent.reference ?? '').toLowerCase();
        case 'documentDate':
            return new Date(firstTx?.documentDate || 0).getTime();
        case 'amount':
            return getParentTotalAmount(parent);
        case 'type':
            return formatTypeLabel(firstTx?.type, t).toLowerCase();
        case 'debitAccount':
            return (formatAccountLabel?.(firstTx?.debitAccount) ?? firstTx?.debitAccount ?? '').toLowerCase();
        case 'creditAccount':
            return (formatAccountLabel?.(firstTx?.creditAccount) ?? firstTx?.creditAccount ?? '').toLowerCase();
        case 'uploadNumber':
            return firstTx?.uploadNumber ?? 0;
        default:
            return parent[key] ?? '';
    }
}

function compareSortValues(valueA, valueB, direction) {
    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return 1;
    if (valueB == null) return -1;
    if (valueA < valueB) return direction === 'asc' ? -1 : 1;
    if (valueA > valueB) return direction === 'asc' ? 1 : -1;
    return 0;
}

function MatchedTable({
    matchData,
    expandedMatchedRows,
    handleMatchedExpandClick,
    handleRemoveMatchClick,
    onRemoveMatches,
    removing = false,
    formatDate,
    beautifyNumber,
    formatAccountLabel,
    t,
    isForeignCurrency,
    direction = 'rtl',
}) {
    const [page, setPage] = useState(0);
    const [groupsPerPage, setGroupsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [columnFilters, setColumnFilters] = useState(EMPTY_FILTERS);
    const [sortConfig, setSortConfig] = useState({ key: 'matchNumber', direction: 'asc' });
    const [selectedIds, setSelectedIds] = useState([]);

    // A refetch replaces the matches, so a selection made against the old ones means nothing.
    useEffect(() => {
        setSelectedIds([]);
    }, [matchData]);

    const lowerSearch = searchTerm.toLowerCase().trim();
    const hasActiveFilters = lowerSearch || Object.values(columnFilters).some((v) => v.trim());

    const handleFilterChange = (field, value) => {
        setColumnFilters((prev) => ({ ...prev, [field]: value }));
        setPage(0);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setColumnFilters(EMPTY_FILTERS);
        setPage(0);
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
        setPage(0);
    };

    const filteredParents = useMemo(() => {
        if (!matchData?.length) return [];
        return matchData.filter(
            (parent) =>
                parentMatchesGlobalSearch(parent, lowerSearch, formatDate, formatAccountLabel, t) &&
                parentMatchesColumnFilters(parent, columnFilters, formatDate, formatAccountLabel, t)
        );
    }, [matchData, lowerSearch, columnFilters, formatDate, formatAccountLabel, t]);

    const sortedParents = useMemo(() => {
        const { key, direction: dir } = sortConfig;
        if (!key) return filteredParents;
        return [...filteredParents].sort((a, b) =>
            compareSortValues(
                getSortValue(a, key, formatDate, formatAccountLabel, t),
                getSortValue(b, key, formatDate, formatAccountLabel, t),
                dir
            )
        );
    }, [filteredParents, sortConfig, formatDate, formatAccountLabel, t]);

    const buildDisplayedRows = useCallback((parent) => {
        const rows = [{ ...parent, level: 0, isSubRow: false }];
        if (!expandedMatchedRows[parent.id]) return rows;

        const externalCopy = parent.externalTransactions || [];
        const internalCopy = parent.internalTransactions || [];

        if (externalCopy.length > 0) {
            rows.push({
                id: `${parent.id}-external-header`,
                parentId: parent.id,
                isSubRow: true,
                isHeader: true,
                level: 1,
                transactionType: t('BankCommands'),
            });
            externalCopy.forEach((tx) => {
                rows.push({
                    ...tx,
                    parentId: parent.id,
                    isSubRow: true,
                    isHeader: false,
                    level: 2,
                    transactionType: 'External',
                });
            });
        }

        if (internalCopy.length > 0) {
            rows.push({
                id: `${parent.id}-internal-header`,
                parentId: parent.id,
                isSubRow: true,
                isHeader: true,
                level: 1,
                transactionType: t('BookCommands'),
            });
            internalCopy.forEach((tx) => {
                rows.push({
                    ...tx,
                    parentId: parent.id,
                    isSubRow: true,
                    isHeader: false,
                    level: 2,
                    transactionType: 'Internal',
                });
            });
        }
        return rows;
    }, [expandedMatchedRows, t]);

    const groups = useMemo(
        () => sortedParents.map((parent) => buildDisplayedRows(parent)),
        [sortedParents, buildDisplayedRows]
    );

    const pageGroups = useMemo(() => {
        const start = page * groupsPerPage;
        return groups.slice(start, start + groupsPerPage);
    }, [groups, page, groupsPerPage]);

    const currentRows = useMemo(() => pageGroups.flat(), [pageGroups]);

    const pageParentIds = useMemo(
        () => pageGroups.map((group) => group[0]?.id).filter(Boolean),
        [pageGroups]
    );
    const allPageSelected = pageParentIds.length > 0
        && pageParentIds.every((id) => selectedIds.includes(id));

    const toggleMatch = (id) => {
        setSelectedIds((prev) => (
            prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
        ));
    };

    // The header box works on the page in view, not on matches the filters are hiding.
    const togglePage = () => {
        setSelectedIds((prev) => (
            allPageSelected
                ? prev.filter((id) => !pageParentIds.includes(id))
                : [...new Set([...prev, ...pageParentIds])]
        ));
    };

    const selectedMatches = useMemo(
        () => (matchData || []).filter((match) => selectedIds.includes(match.id)),
        [matchData, selectedIds]
    );

    const renderSortableHeader = (field, label, sortable = true) => {
        if (!sortable) {
            return <TableCell sx={headerStyle}>{label}</TableCell>;
        }
        const isActive = sortConfig.key === field;
        const dir = isActive ? sortConfig.direction : 'asc';
        return (
            <TableCell sx={headerStyle}>
                <TableSortLabel
                    active={isActive}
                    direction={dir}
                    onClick={() => handleSort(field)}
                    sx={{
                        color: "var(--Foundation-Blue-Normal, #304FFF)",
                        '&.Mui-active': { color: "var(--Foundation-Blue-Normal, #304FFF)" },
                        '& .MuiTableSortLabel-icon': {
                            color: "var(--Foundation-Blue-Normal, #304FFF) !important",
                        },
                    }}
                >
                    {label}
                </TableSortLabel>
            </TableCell>
        );
    };

    const renderFilterInput = (field, placeholder) => (
        <TableCell sx={filterCellStyle}>
            <TextField
                size="small"
                fullWidth
                placeholder={placeholder || t('Filter')}
                value={columnFilters[field]}
                onChange={(e) => handleFilterChange(field, e.target.value)}
                variant="outlined"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        fontSize: '12px',
                        backgroundColor: '#fff',
                    },
                }}
                inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
            />
        </TableCell>
    );

    const isInternalSubRow = (row) =>
        row.isSubRow && !row.isHeader && row.transactionType === 'Internal';

    const renderMatchNumber = (row) => (!row.isSubRow ? row.matchNumber : '');
    const renderTransactionDate = (row) => {
        if (row.isSubRow && !row.isHeader) return formatDate(row.documentDate);
        if (!row.isSubRow) return formatDate(row.transactionDate);
        return '';
    };
    const renderMatchDate = (row) => (!row.isSubRow ? formatDate(row.matchDate) : '');
    const renderDescription = (row) => {
        const indentation = row.level * 20;
        if (row.isHeader) {
            return (
                <div style={{ paddingLeft: indentation, fontWeight: 'bold' }}>
                    {t(row.transactionType)}
                </div>
            );
        }
        if (row.isSubRow) {
            return <div style={{ paddingLeft: indentation }}>{row.description}</div>;
        }
        // Collapsed: how many entries sit on each side, and what they are about.
        return (
            <div style={{ fontSize: '12px', lineHeight: 1.4 }}>
                <div style={{ fontWeight: 600 }}>{sideCountsLabel(row, t)}</div>
                <div style={{ color: '#666' }}>
                    {summarizeValues(distinctOf(row, (tx) => tx.description))}
                </div>
            </div>
        );
    };
    const renderReference = (row) => {
        if (row.isHeader) return '';
        if (row.isSubRow) {
            return [row.reference, row.reference2].filter(Boolean).join(' / ');
        }
        return summarizeValues(distinctOf(row, (tx) => tx.reference));
    };
    const renderDocumentDate = (row) =>
        (row.isSubRow && !row.isHeader ? formatDate(row.documentDate) : '');
    const renderAmount = (row) => {
        if (row.isSubRow && !row.isHeader) return beautifyNumber(row.amount);
        if (row.externalTransactions?.length) {
            return beautifyNumber(getParentTotalAmount(row));
        }
        if (row.internalTransactions?.length) {
            return beautifyNumber(getParentTotalAmount(row));
        }
        return '';
    };
    const renderType = (row) => {
        if (row.isHeader) return '';
        if (row.isSubRow) return row.type ? formatTypeLabel(row.type, t) : '';
        return summarizeValues(distinctOf(row, (tx) => (tx.type ? formatTypeLabel(tx.type, t) : '')));
    };
    const renderUploadNumber = (row) => {
        if (row.isHeader) return '';
        if (row.isSubRow) return row.uploadNumber;
        return summarizeValues(distinctOf(row, (tx) => tx.uploadNumber));
    };
    const renderAccount = (row, field) => {
        if (row.isHeader) return '';
        if (row.isSubRow) {
            return isInternalSubRow(row) && formatAccountLabel ? formatAccountLabel(row[field]) : '';
        }
        // Collapsed: the contra accounts of the book side, which is where they live.
        const accounts = [...new Set(
            (row.internalTransactions || []).map((tx) => tx[field]).filter(Boolean)
        )];
        return summarizeValues(accounts.map((id) => (formatAccountLabel ? formatAccountLabel(id) : id)));
    };
    const renderDebitAccount = (row) => renderAccount(row, 'debitAccount');
    const renderCreditAccount = (row) => renderAccount(row, 'creditAccount');

    return (
        <>
            <Box
                className="document-filter-row"
                dir={direction}
                sx={{ mb: 1.5 }}
            >
                <TextField
                    size="small"
                    dir={direction}
                    placeholder={t('Search')}
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                    className="document-search-bar"
                    variant="standard"
                    InputProps={{
                        disableUnderline: true,
                        className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon className="document-search-icon" />
                            </InputAdornment>
                        ),
                    }}
                />
                {hasActiveFilters && (
                    <Button
                        size="small"
                        startIcon={<FilterAltOff />}
                        onClick={clearFilters}
                        sx={{ color: '#304FFF', whiteSpace: 'nowrap' }}
                    >
                        {t('ClearFilters')}
                    </Button>
                )}
                <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                    {t('Matched')}: {sortedParents.length}
                </Typography>
                {onRemoveMatches && (
                    <ActionChip
                        label={`${t('CancelSelectedMatches')} (${selectedMatches.length})`}
                        icon={<CancelRounded fontSize="small" />}
                        borderColor="#D32F2F"
                        direction={direction}
                        disabled={selectedMatches.length === 0 || removing}
                        onClick={() => onRemoveMatches(selectedMatches)}
                    />
                )}
            </Box>

            <TableContainer
                component={Paper}
                className="bank-workspace-scrollbar"
                style={{ height: 'calc(100% - 56px)' }}
            >
                <Table stickyHeader size="small" aria-label="matched-table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headerStyle, width: 44, padding: 0 }}>
                                <Checkbox
                                    size="small"
                                    checked={allPageSelected}
                                    indeterminate={!allPageSelected && pageParentIds.some((id) => selectedIds.includes(id))}
                                    onChange={togglePage}
                                />
                            </TableCell>
                            {renderSortableHeader('matchNumber', t('MatchNumber'))}
                            {renderSortableHeader('transactionDate', t('TransactionDate'))}
                            {renderSortableHeader('matchDate', t('MatchDate'))}
                            {renderSortableHeader('description', t('Description'))}
                            {renderSortableHeader('reference', t('Reference'))}
                            {renderSortableHeader('documentDate', t('Document Date'))}
                            {renderSortableHeader('amount', isForeignCurrency ? t('CurrencyAmount') : t('Amount'))}
                            {renderSortableHeader('type', t('Type'))}
                            {renderSortableHeader('debitAccount', t('Debit Account'))}
                            {renderSortableHeader('creditAccount', t('Credit Account'))}
                            {renderSortableHeader('uploadNumber', t('UploadNumber'))}
                            <TableCell sx={headerStyle}>{t('Actions')}</TableCell>
                            <TableCell sx={headerStyle} />
                        </TableRow>
                        <TableRow>
                            <TableCell sx={filterCellStyle} />
                            {renderFilterInput('matchNumber', '#')}
                            {renderFilterInput('transactionDate')}
                            {renderFilterInput('matchDate')}
                            {renderFilterInput('description')}
                            {renderFilterInput('reference')}
                            {renderFilterInput('documentDate')}
                            {renderFilterInput('amount')}
                            {renderFilterInput('type')}
                            {renderFilterInput('debitAccount')}
                            {renderFilterInput('creditAccount')}
                            {renderFilterInput('uploadNumber')}
                            <TableCell sx={filterCellStyle} colSpan={2} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currentRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={14} align="center" sx={{ py: 4, color: '#888' }}>
                                    {hasActiveFilters ? t('NoResultForYourSearch') : t('NoData')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            currentRows.map((row) => (
                                <TableRow key={row.id ?? `${row.parentId}-${row.matchNumber}`} sx={getRowSx(row)}>
                                    <TableCell align="center" sx={{ padding: 0 }}>
                                        {!row.isSubRow && (
                                            <Checkbox
                                                size="small"
                                                checked={selectedIds.includes(row.id)}
                                                onChange={() => toggleMatch(row.id)}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">{renderMatchNumber(row)}</TableCell>
                                    <TableCell align="center">{renderTransactionDate(row)}</TableCell>
                                    <TableCell align="center">{renderMatchDate(row)}</TableCell>
                                    <TableCell align="center">{renderDescription(row)}</TableCell>
                                    <TableCell align="center">{renderReference(row)}</TableCell>
                                    <TableCell align="center">{renderDocumentDate(row)}</TableCell>
                                    <TableCell align="center">{renderAmount(row)}</TableCell>
                                    <TableCell align="center">{renderType(row)}</TableCell>
                                    <TableCell align="center">{renderDebitAccount(row)}</TableCell>
                                    <TableCell align="center">{renderCreditAccount(row)}</TableCell>
                                    <TableCell align="center">{renderUploadNumber(row)}</TableCell>
                                    <TableCell align="center">
                                        {!row.isSubRow && (
                                            <Tooltip title={t('RemoveMatch')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveMatchClick(row)}
                                                >
                                                    <CancelRounded style={{ color: '#304FFF' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {!row.isSubRow && (
                                            (row.externalTransactions?.length || row.internalTransactions?.length) ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleMatchedExpandClick(row.id)}
                                                >
                                                    {expandedMatchedRows[row.id] ? <ExpandLess /> : <ExpandMore />}
                                                </IconButton>
                                            ) : null
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[25, 50, 100, 200]}
                component="div"
                count={groups.length}
                rowsPerPage={groupsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setGroupsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                labelRowsPerPage={t('RowsPerPage')}
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} ${t('of')} ${count}`
                }
            />
        </>
    );
}

export default MatchedTable;
